import React from 'react';
import axios from 'axios';
import Dropzone from "react-dropzone";
import { Button, TextField, InputLabel, MenuItem, FormControl, Select, Card, CardContent, CardActions, OutlinedInput, Typography, Grid } from '@mui/material';
import * as ETVConstants from '../ETVConstants';
import dayjs from 'dayjs';

class ContentSettings extends React.Component {
  constructor(props) {
    super(props);
  }

  state = {
    content_id: undefined,
    cardMediaUrl: undefined,
    showingTime: 20,
    description: '',
    content_category: '',
    needs_save: false,
    loading_thumbnail: false,
  };

  componentDidMount() {
    this.initSettings(this.props);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.library_element !== this.props.library_element) {
      this.initSettings();
    }
  }

  initSettings() {
    const elem = this.props.library_element;
    if (!elem) return;

    const { content_id, showing_time, description, content_category } = elem;
    const cardMediaUrl = this.getThumbnail(elem);

    this.setState({
      content_id,
      content_category,
      cardMediaUrl,
      showingTime: showing_time,
      description,
      needs_save: false
    });
  }

  updateContentRegistry(url) {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");

    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(() => {
        this.setState({ isLoaded: false, needs_save: false });
      });
  }

  getThumbnail(elem) {
    let imageUrl = elem.thumbnail;
    if (!imageUrl) {
      const filename = elem.filename.toLowerCase();
      if (filename.endsWith(".mp4") || filename.endsWith(".mpeg") || filename.endsWith(".mpg")) {
        imageUrl = "/image_assets/content/movie_placeholder.png";
      } else if (filename.endsWith(".html") || filename.endsWith(".htm")) {
        imageUrl = "/image_assets/content/html_placeholder.png";
      }
    }
    return imageUrl;
  }

  handleShowingTimeChange = (event) => {
    this.setState({ showingTime: event.target.value, needs_save: true });
  };

  saveSettings() {
    const showingTime = Math.max(5, this.state.showingTime);
    let url = `${ETVConstants.getServerBase()}/content_library?action=set_config&content_id=${this.state.content_id}&showing_time=${showingTime}`;

    if (this.state.description) {
      url += `&description=${encodeURIComponent(this.state.description)}`;
    }
    if (this.state.content_category) {
      url += `&content_category=${this.state.content_category}`;
    }

    this.setState({ showingTime });
    this.updateContentRegistry(url);
  }

  registerThumbnail(contentID, fileUrl) {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    const url = `${ETVConstants.getServerBase()}/content_library?action=register_thumbnail&content_id=${contentID}&thumbnail=${encodeURIComponent(fileUrl)}`;

    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(() => {
        this.setState({ loading_thumbnail: false, cardMediaUrl: fileUrl });
      });
  }

  onDrop = (files) => {
    const targetDir = `/${this.props.user_id}/content_library`;
    this.setState({ loading_thumbnail: true });
    files.forEach(file => this.uploadFileToGCS(file, targetDir));
  };

  uploadFileToGCS(file, targetDir) {
    const filename = `${Math.floor(Math.random() * 100000 + 1)}_${file.name}`;
    const url = `${ETVConstants.getServerBase()}/gcs_upload?action=request_upload&target_dir=${targetDir}&filename=${filename}`;

    fetch(url)
      .then(res => res.json())
      .then(result => {
        const formData = new FormData();
        Object.entries(result.fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append('file', file);

        axios.post(result.url, formData, {
          headers: { 'content-type': 'multipart/form-data' }
        }).then(() => {
          this.givePublicAccess(targetDir, filename);
        });
      });
  }

  givePublicAccess(targetDir, filename) {
    const url = `${ETVConstants.getServerBase()}/gcs_upload?action=public_access&target_dir=${targetDir}&filename=${filename}`;
    fetch(url)
      .then(res => res.json())
      .then(result => {
        const contentID = this.props.library_element.content_id;
        this.registerThumbnail(contentID, result.url);
      });
  }

  render() {
    const { cardMediaUrl, content_category, showingTime, description, needs_save, loading_thumbnail } = this.state;
    const editable = this.props.editable;
    const isNewsProvider = this.props.user_roles?.includes('admin.content.contribute_marketplace');

    return (
      <Grid container spacing={3}>
        {/* Thumbnail & Dropzone */}
        <Grid item sm={6} xs={12}>
          <Card>
            <CardContent>
              {cardMediaUrl && !loading_thumbnail && (
                <img src={ETVConstants.ON_PREMISE_VERSION ? this.makeImageUrlRelative(cardMediaUrl) : cardMediaUrl} alt="thumbnail" style={{ width: 300, height: 'auto' }} />
              )}
              {isNewsProvider && (
                <div style={{ width: '100%', marginTop: 20 }}>
                  <Dropzone
                    onDrop={this.onDrop}
                    disabled={false}
                    accept={{ 'image/*': [".jpg", ".jpeg", ".gif", ".png"] }}
                    multiple={false}
                    minSize={1024}
                    maxSize={2000000}>
                    {({ getRootProps, getInputProps }) => (
                      <div {...getRootProps({ className: "dropzone" })}>
                        <input {...getInputProps()} />
                        <Typography variant="body2">{ETVConstants.trans("drag_and_drop_or_click_to_upload_thumbnail_max_2mb")}</Typography>
                      </div>
                    )}
                  </Dropzone>
                </div>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Content Settings */}
        <Grid item sm={6} xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                {ETVConstants.trans("general_settings")}
              </Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel id="content_category">{ETVConstants.trans("content_category")}</InputLabel>
                <Select
                  labelId="content_category"
                  disabled={!editable}
                  value={content_category}
                  onChange={(e) => this.setState({ content_category: e.target.value, needs_save: true })}
                  input={<OutlinedInput label={ETVConstants.trans("content_category")} />}
                >
                  <MenuItem value='---'>---</MenuItem>
                  <MenuItem value='info'>{ETVConstants.trans("information")}</MenuItem>
                  <MenuItem value='news'>{ETVConstants.trans("news")}</MenuItem>
                  <MenuItem value='entertainment'>{ETVConstants.trans("entertainment")}</MenuItem>
                  <MenuItem value='education'>{ETVConstants.trans("education")}</MenuItem>
                  <MenuItem value='inspiration'>{ETVConstants.trans("inspiration")}</MenuItem>
                  <MenuItem value='music'>{ETVConstants.trans("music")}</MenuItem>
                  <MenuItem value='template'>{ETVConstants.trans("template")}</MenuItem>
                </Select>
              </FormControl>

              <TextField
                margin="normal"
                label={ETVConstants.trans("showing_time_s")}
                fullWidth
                size="small"
                value={showingTime}
                disabled={!editable}
                type="number"
                onChange={this.handleShowingTimeChange}
              />

              <TextField
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                fullWidth
                disabled={!editable}
                label={ETVConstants.trans("description")}
                value={description}
                onChange={(e) => this.setState({ description: e.target.value, needs_save: true })}
              />
            </CardContent>
            <CardActions>
              {editable && (
                <Button
                  size='small'
                  variant='contained'
                  color='success'
                  disabled={!needs_save}
                  onClick={() => this.saveSettings()}
                >
                  {ETVConstants.trans("save")}
                </Button>
              )}
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    );
  }
}

export default ContentSettings;
